import React from "react";
import { MDBCarousel, MDBCarouselItem } from "mdb-react-ui-kit";
import "./CustomCarousel.css";
import { doFetch } from "Projects/CC_cubi_clan_project/Utils/ConnectionManager";
import { getFileVariants, mfmFramedUrl, mfmVariantUrl } from "Projects/CC_cubi_clan_project/Utils/MfmService";

const CAROUSEL_FORMAT_KEY = "ratio:1246:400";
const CAROUSEL_WIDTH = 1246;
const CAROUSEL_HEIGHT = 400;

/** ---------- Helpers de normalización ---------- */
const toDataUri = (b64) =>
  b64 ? (String(b64).startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`) : "";

const normalizeStatus = (s) => {
  const v = String(s ?? "").trim().toLowerCase();
  if (v === "published" || v === "active") return "published";
  if (v === "draft" || v === "inactive") return "draft";
  return v ? v : "draft";
};

const coerceActive = (row) => {
  if (row?.status != null) return normalizeStatus(row.status) === "published";
  const a = row?.active;
  return a === 1 || a === "1" || a === true || String(a).toLowerCase() === "true";
};

const fromBackendItem = (row) => ({
  id: row?.IDCarousel ?? row?.iDCarousel ?? row?.id ?? null,
  image: row?.imageUuid
    ? mfmFramedUrl(row.imageUuid, 1246, 400, CAROUSEL_FORMAT_KEY)
    : toDataUri(row?.base64img ?? row?.imageBase64 ?? row?.image),
  imageUuid: row?.imageUuid ?? null,
  title: row?.title ?? "",
  description: row?.description ?? "",
  link: row?.link ?? "",
  sortOrder: Number(row?.sortOrder) || 0,
  status: row?.status ?? (coerceActive(row) ? "published" : "draft"),
  active: coerceActive(row),
});

// Resuelve una imagen independientemente de si es data URI, URL HTTP o base64 plano
const resolveImageSrc = (img) => {
  if (!img) return "";
  const s = String(img);
  if (s.startsWith("data:") || s.startsWith("http://") || s.startsWith("https://") || s.startsWith("/")) return s;
  return `data:image/jpeg;base64,${s}`;
};

// Si te pasan items ya formateados, intenta completarlos
const normalizeIncoming = (it, idx) => {
  if (it && (it.id != null || it.image != null)) {
    const status = it.status ? normalizeStatus(it.status) : (it.active ? "published" : "draft");
    const preResolvedImage = resolveImageSrc(it.image ?? it.imageBase64 ?? it.base64img);
    const fallbackFramed = it.imageUuid
      ? mfmFramedUrl(it.imageUuid, CAROUSEL_WIDTH, CAROUSEL_HEIGHT, CAROUSEL_FORMAT_KEY)
      : "";
    return {
      id: it.id ?? idx,
      // Si la capa superior ya resolvió una URL de variante, la respetamos.
      image: preResolvedImage || fallbackFramed,
      title: it.title ?? "",
      description: it.description ?? "",
      link: it.link ?? "",
      sortOrder: Number(it.sortOrder) || 0,
      status,
      active: status === "published",
    };
  }
  return fromBackendItem(it || {});
};

const sortByOrder = (arr = []) =>
  arr.slice().sort((a, b) => {
    const ax = Number(a.sortOrder) || Number.MAX_SAFE_INTEGER;
    const bx = Number(b.sortOrder) || Number.MAX_SAFE_INTEGER;
    return ax - bx;
  });

const isItemActive = (it) =>
  it?.active === true || normalizeStatus(it?.status) === "published";

const computeSlides = (items = [], filterActive = true) => {
  const normalized = items.map(normalizeIncoming);
  const filtered = filterActive ? normalized.filter(isItemActive) : normalized;
  return sortByOrder(filtered);
};

export class CustomCarousel extends React.Component {
  constructor(props) {
    super(props);
    const items = props.items ?? [];
    const filterActive = props.filterActive ?? true;
    const slides = computeSlides(items, filterActive);

    this.state = {
      items,
      slides,
      showControls: props.showControls ?? true,
      showIndicators: props.showIndicators ?? true,
      filterActive,
      className: props.className ?? "",
      signature: CustomCarousel.computeSignature(slides, filterActive),
      loading: false,
    };
    this._variantRequestId = 0;
  }

  componentDidMount() {
    this.loadItems();
  }

  async loadItems() {
    this.setState({ loading: true });
    this.props.onLoading?.(true);
    try {
      const response = await doFetch("MUGIN", "getCarousel", { method: "GET" });
      const data = await response.json().catch(() => ({}));
      const rawItems = data?.DATA ?? [];
      const items = rawItems.map(fromBackendItem);
      const itemsResolved = await this.resolveCarouselVariants(items);
      const slides = computeSlides(itemsResolved, this.state.filterActive);
      const signature = CustomCarousel.computeSignature(slides, this.state.filterActive);
      this.setState({ items: itemsResolved, slides, signature });
    } catch (error) {
      console.error("Error al obtener los elementos del carrusel:", error);
      this.setState({ items: [], slides: [], signature: "err|empty" });
    } finally {
      this.setState({ loading: false });
      this.props.onLoading?.(false);
    }
  }

  resolveCarouselVariants = async (items) => {
    const requestId = ++this._variantRequestId;
    const resolved = await Promise.all(
      (items || []).map(async (item) => {
        const uuid = item?.imageUuid;
        if (!uuid) return item;
        try {
          const variants = await getFileVariants(uuid);
          if (requestId !== this._variantRequestId) return item;
          const persisted = (variants || []).find((v) => {
            const key = String(v?.variantKey || "");
            const w = Number(v?.width);
            const h = Number(v?.height);
            if (key === `framed:${CAROUSEL_FORMAT_KEY}:${CAROUSEL_WIDTH}x${CAROUSEL_HEIGHT}`) {
              return true;
            }
            return key.startsWith("framed:")
              && Number.isFinite(w)
              && Number.isFinite(h)
              && Math.round(w) === CAROUSEL_WIDTH
              && Math.round(h) === CAROUSEL_HEIGHT;
          });

          if (persisted?.variantKey) {
            return {
              ...item,
              image: mfmVariantUrl(uuid, String(persisted.variantKey)),
            };
          }

          return {
            ...item,
            image: mfmFramedUrl(uuid, CAROUSEL_WIDTH, CAROUSEL_HEIGHT, CAROUSEL_FORMAT_KEY),
          };
        } catch {
          return {
            ...item,
            image: mfmFramedUrl(uuid, CAROUSEL_WIDTH, CAROUSEL_HEIGHT, CAROUSEL_FORMAT_KEY),
          };
        }
      }),
    );
    return resolved;
  };

  // Firma basada en lo que realmente se pinta (slides)
  static computeSignature(slides = [], filterActive = true) {
    const base =
      slides
        .map((it, idx) => [
          it?.id ?? idx,
          it?.active ? 1 : 0,
          it?.status || "",
          it?.sortOrder || 0,
          (it?.image && it.image.length) || 0,
          it?.title || "",
        ].join(":"))
        .join("|") || "empty";
    return `${filterActive ? "fa1" : "fa0"}|len=${slides.length}|${base}`;
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    const items = nextProps.items ?? prevState.items ?? [];
    const filterActive = nextProps.filterActive ?? prevState.filterActive ?? true;
    const slides = computeSlides(items, filterActive);
    const nextSignature = CustomCarousel.computeSignature(slides, filterActive);

    if (
      nextSignature !== prevState.signature ||
      (nextProps.showControls ?? true) !== prevState.showControls ||
      (nextProps.showIndicators ?? true) !== prevState.showIndicators ||
      (nextProps.className ?? "") !== prevState.className
    ) {
      return {
        items,
        slides,
        filterActive,
        showControls: nextProps.showControls ?? true,
        showIndicators: nextProps.showIndicators ?? true,
        className: nextProps.className ?? "",
        signature: nextSignature,
      };
    }
    return null;
  }

  render() {
    const { slides, showControls, showIndicators, className, signature } = this.state;

    return (
      <MDBCarousel
        key={signature} // remonta cuando cambia el contenido real
        showControls={showControls}
        showIndicators={showIndicators}
        className={"custom-carousel " + className}
      >
        {slides.map((item, idx) => (
            <MDBCarouselItem
              key={item.id ?? idx}
              className="custom-carousel-image"
              itemId={idx + 1}
              src={item.image}
              alt={item.title || `Slide ${idx + 1}`}
              draggable={false}
              captionClassName="custom-carousel-caption"
            >
            {(item.title || item.description || item.link) && (
              <>
                {item.title && <h3 className="carousel-caption-title">{item.title}</h3>}
                {item.description && <p className="carousel-caption-text">{item.description}</p>}
                {item.link && (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="carousel-caption-link"
                  >
                    Más info
                  </a>
                )}
              </>
            )}
            </MDBCarouselItem>
        ))}
      </MDBCarousel>
    );
  }
}
